import { useState, FormEvent, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService, ChatMessage } from '../lib/api';

export default function Chat() {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<Array<{ role: string; content: string; id?: string }>>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { user, token, logout } = useAuth();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!message.trim() || !user || !token) return;

    const userMessage = { role: 'user', content: message, id: Date.now().toString() };
    setChatHistory(prev => [...prev, userMessage]);
    
    const currentMessage = message;
    setMessage('');
    setIsTyping(true);
    setError(null);
    
    try {
      const chatForAPI: ChatMessage[] = chatHistory.map(msg => ({
        timestamp: new Date().toISOString(),
        isUser: msg.role === 'user',
        text: msg.content
      }));

      const response = await apiService.sendUserQuery({
        query: currentMessage,
        chat: chatForAPI,
        userUID: user.id
      }, token);
      
      const botResponse = { 
        role: 'assistant', 
        content: response.response,
        id: Date.now().toString()
      };
      setChatHistory(prev => [...prev, botResponse]);
      
    } catch (error) {
      console.error('Chat API error:', error);
      setError('Failed to get response. Please try again.');
      const errorResponse = {
        role: 'assistant',
        content: 'Sorry, I encountered an error connecting to the server. Please try again later.',
        id: Date.now().toString()
      };
      setChatHistory(prev => [...prev, errorResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleFeedback = async (messageId: string, feedback: 'helpful' | 'not_helpful', query: string, answer: string) => {
    if (!user) return;
    
    try {
      await apiService.sendFeedback({
        userID: user.id,
        messageID: messageId,
        feedback,
        query,
        answer,
        timestamp: new Date().toISOString()
      });
      console.log('Feedback sent successfully');
    } catch (error) {
      console.error('Failed to send feedback:', error);
    }
  };

  const clearChat = () => {
    setChatHistory([]);
    setError(null);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'var(--color-bg)',
      fontFamily: 'var(--font-family)',
      position: 'relative'
    }}>
      {/* Top Right Controls */}
      <div style={{
        position: 'absolute',
        top: '24px',
        right: '24px',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        {chatHistory.length > 0 && (
          <button
            onClick={clearChat}
            style={{
              background: 'none',
              border: '1px solid var(--color-border)',
              padding: '8px 16px',
              fontSize: '14px',
              cursor: 'pointer',
              color: 'var(--color-text)',
              fontWeight: '300'
            }}
          >
            Clear
          </button>
        )}
        
        {/* User Menu */}
        <div style={{ position: 'relative' }} ref={userMenuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            style={{
              background: 'none',
              border: '1px solid var(--color-border)',
              padding: '8px 16px',
              fontSize: '14px',
              cursor: 'pointer',
              color: 'var(--color-text)',
              fontWeight: '300',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {user?.name?.split(' ')[0] || 'User'}
            <span style={{ fontSize: '12px', opacity: '0.6' }}>
              {showUserMenu ? '▲' : '▼'}
            </span>
          </button>
          
          {showUserMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: '0',
              marginTop: '4px',
              backgroundColor: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              minWidth: '120px',
              zIndex: 20
            }}>
              <button
                onClick={() => {
                  logout();
                  setShowUserMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: 'var(--color-text)',
                  fontWeight: '300',
                  textAlign: 'left'
                }}
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          position: 'absolute',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'var(--color-error)',
          fontSize: '14px',
          zIndex: 10,
          fontWeight: '300'
        }}>
          {error}
        </div>
      )}

      {/* Messages */}
      <div 
        ref={chatContainerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '120px 24px 120px 24px',
          maxWidth: '800px',
          margin: '0 auto',
          width: '100%'
        }}
      >
        {chatHistory.length === 0 ? (
          <div style={{
            textAlign: 'center',
            paddingTop: '120px',
            color: 'var(--color-text-secondary)'
          }}>
            <h1 style={{ 
              margin: '0 0 16px 0', 
              color: 'var(--color-text)',
              fontSize: '32px',
              fontWeight: '300',
              letterSpacing: '-1px'
            }}>Memory</h1>
            <p style={{
              fontSize: '14px',
              color: 'var(--color-text-secondary)',
              fontWeight: '300'
            }}>Ask me anything about your memories</p>
          </div>
        ) : (
          <>
            {chatHistory.map((msg, index) => (
              <div key={index} style={{
                marginBottom: '32px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start'
              }}>
                {/* Message label */}
                <div style={{
                  fontSize: '12px',
                  color: 'var(--color-text-tertiary)',
                  marginBottom: '4px',
                  fontWeight: '300',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {msg.role === 'user' ? 'You' : 'Memory'}
                </div>
                
                {/* Message content */}
                <div style={{
                  maxWidth: '80%',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  color: 'var(--color-text)',
                  fontWeight: '300',
                  padding: msg.role === 'user' ? '16px 20px' : '16px 0',
                  backgroundColor: msg.role === 'user' ? 'var(--color-accent-light)' : 'transparent',
                  border: msg.role === 'user' ? '1px solid var(--color-border)' : 'none'
                }}>
                  {msg.content}
                </div>
                
                {/* Feedback buttons for assistant messages */}
                {msg.role === 'assistant' && (
                  <div style={{
                    marginTop: '12px',
                    display: 'flex',
                    gap: '16px',
                    fontSize: '12px',
                    opacity: '0.5'
                  }}>
                    <button
                      onClick={() => {
                        const userMsg = chatHistory[index - 1];
                        if (userMsg && msg.id) {
                          handleFeedback(msg.id, 'helpful', userMsg.content, msg.content);
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--color-text-secondary)',
                        fontWeight: '300',
                        fontSize: '12px',
                        padding: '4px 8px'
                      }}
                    >
                      Helpful
                    </button>
                    <button
                      onClick={() => {
                        const userMsg = chatHistory[index - 1];
                        if (userMsg && msg.id) {
                          handleFeedback(msg.id, 'not_helpful', userMsg.content, msg.content);
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--color-text-secondary)',
                        fontWeight: '300',
                        fontSize: '12px',
                        padding: '4px 8px'
                      }}
                    >
                      Not helpful
                    </button>
                  </div>
                )}
              </div>
            ))}
            
            {isTyping && (
              <div style={{
                marginBottom: '32px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: 'var(--color-text-tertiary)',
                  marginBottom: '4px',
                  fontWeight: '300',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Memory
                </div>
                <div style={{
                  fontSize: '16px',
                  color: 'var(--color-text-secondary)',
                  fontWeight: '300',
                  fontStyle: 'italic'
                }}>
                  Thinking...
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Input */}
      <div style={{
        position: 'fixed',
        bottom: '0',
        left: '0',
        right: '0',
        padding: '24px',
        backgroundColor: 'var(--color-bg)',
        borderTop: '1px solid var(--color-border)'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <form onSubmit={handleSubmit}>
            <div style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: '1px'
            }}>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e as any);
                  }
                }}
                placeholder="Ask me about your memories..."
                style={{
                  flex: 1,
                  padding: '20px',
                  border: '1px solid var(--color-border)',
                  fontSize: '16px',
                  lineHeight: '1.4',
                  resize: 'none',
                  fontFamily: 'var(--font-family)',
                  backgroundColor: 'var(--color-bg)',
                  color: 'var(--color-text)',
                  fontWeight: '300',
                  minHeight: '60px',
                  maxHeight: '120px',
                  outline: 'none'
                }}
                rows={1}
              />
              <button
                type="submit"
                disabled={!message.trim() || isTyping}
                style={{
                  padding: '20px 24px',
                  backgroundColor: message.trim() && !isTyping ? 'var(--color-accent)' : 'var(--color-bg)',
                  color: message.trim() && !isTyping ? 'var(--color-accent-secondary)' : 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)',
                  fontSize: '14px',
                  fontWeight: '300',
                  cursor: message.trim() && !isTyping ? 'pointer' : 'not-allowed',
                  outline: 'none',
                  minWidth: '80px'
                }}
              >
                {isTyping ? 'Sending...' : 'Send'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}